import { useCallback, useEffect, useMemo, useState } from 'react';

import { Category } from '../../EligibleServices.types';
import { useEligibleServicesStore } from '../stores';

export const useCategoryActions = (formattedData?: Category[]) => {
  const { state, setState } = useEligibleServicesStore();
  const eligibleServicesIdsArray = useMemo(() => state?.eligibleServicesIds || [], [state]);
  
  const [expandedCategoryIdArray, setExpandedCategoryId] = useState<string[]>([]);
  const [categoryIdArray, setCategoryIdArray] = useState<string[]>([]);
  const [indeterminateCategoryIdArray, setIndeterminateCategoryIdArray] = useState<string[]>([]);

  const handleCategorySelect = useCallback((id: string) => {
    setCategoryIdArray((prevArray: string[]) => {
      const isSelected = prevArray.includes(id);
      const updatedArray = isSelected ? prevArray.filter((itemId) => itemId !== id) : [...prevArray, id];

      const category = formattedData?.find((category) => category.id === id);
      if (category) {
        const serviceIds = category.services.map((service) => service.id);
        if (isSelected) {
          // Remove service IDs from eligibleServicesIdsArray
          const updatedEligibleServicesIds = eligibleServicesIdsArray?.filter((serviceId) => !serviceIds?.includes(serviceId));
          setState({ eligibleServicesIds: updatedEligibleServicesIds });
        } else {
          // Add service IDs to eligibleServicesIdsArray
          const updatedEligibleServicesIds = [...eligibleServicesIdsArray, ...serviceIds];
          setState({ eligibleServicesIds: updatedEligibleServicesIds });
        }
      }

      return updatedArray;
    });
  }, [formattedData, eligibleServicesIdsArray, setState]);

  const handleSetExpandedCategory = useCallback((id: string) => {
    setExpandedCategoryId((prevArray: string[]) => {
      if (prevArray.includes(id)) {
        return prevArray.filter((itemId) => itemId !== id);
      } else {
        return [...prevArray, id];
      }
    });
  }, []);

  useEffect(() => {
    formattedData?.forEach((category) => {
      const serviceIds = category?.services?.map((service) => service.id);
      const allServicesSelected = serviceIds?.every((serviceId) => eligibleServicesIdsArray?.includes(serviceId));
      const someServicesSelected = serviceIds?.some((serviceId) => eligibleServicesIdsArray?.includes(serviceId));

      setCategoryIdArray((prevArray) => {
        if (allServicesSelected && !prevArray?.includes(category.id)) {
          return [...prevArray, category.id];
        } else if (!allServicesSelected && prevArray?.includes(category.id)) {
          return prevArray?.filter((itemId) => itemId !== category.id);
        }
        return prevArray;
      });

      setIndeterminateCategoryIdArray((prevArray) => {
        if (someServicesSelected && !allServicesSelected && !prevArray?.includes(category.id)) {
          return [...prevArray, category.id];
        } else if ((!someServicesSelected || allServicesSelected) && prevArray?.includes(category.id)) {
          return prevArray?.filter((itemId) => itemId !== category.id);
        }
        return prevArray;
      });
    });
  }, [eligibleServicesIdsArray, formattedData]);

  return {
    categoryIdArray,
    expandedCategoryIdArray,
    indeterminateCategoryIdArray,
    handleSetExpandedCategory,
    handleCategorySelect,
  };
};
